import { useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { baselineMeasurementService } from '../../../Domain/BaselineMeasurements/Services';
import type { Task, SearchTasksResponse } from '../../../types/BaselineMeasurements';

interface UseTaskPollingOptions {
  comparisonId: string;
  maxAttempts?: number;
  pollingInterval?: number;
  onSuccess?: (data: SearchTasksResponse) => void;
  onTimeout?: () => void;
  onError?: (error: Error) => void;
}

interface UseTaskPollingReturn {
  startPolling: () => void;
  stopPolling: () => void;
  isPolling: boolean;
  tasksData: SearchTasksResponse | undefined;
  attempts: number;
  maxAttempts: number;
  isComplete: boolean;
  isTimeout: boolean;
  isLoading: boolean;
  error: Error | null;
  reset: () => void;
  hasRunningTasks: boolean;
  allTasksCompleted: boolean;
}

const QUERY_KEY = 'searchComparisonTasks';

const hasRunningTasks = (tasks: Task[] | undefined): boolean => {
  return (
    tasks?.some(task => task.status === 'pending' || task.status === 'queued' || task.status === 'processing') ?? false
  );
};

export const useTaskPolling = (options: UseTaskPollingOptions): UseTaskPollingReturn => {
  const { comparisonId, maxAttempts = 10, pollingInterval = 2000, onSuccess, onTimeout, onError } = options;
  const [shouldPoll, setShouldPoll] = useState(false);
  const attemptsRef = useRef(0);
  const hasCalledSuccessRef = useRef(false);

  const tasksStatus = useQuery<SearchTasksResponse>({
    queryKey: [QUERY_KEY, comparisonId],
    queryFn: async () => {
      const response = await baselineMeasurementService.searchComparisonTasks(comparisonId);
      return response.data;
    },
    enabled: shouldPoll && !!comparisonId,
    refetchInterval: data => {
      if (!data) {
        return pollingInterval;
      }
      const tasksRunning = hasRunningTasks(data.tasks);
      const hasAttemptsLeft = attemptsRef.current < maxAttempts;
      if (tasksRunning) {
        attemptsRef.current += 1;

        if (hasAttemptsLeft) {
          return pollingInterval; // Continuar polling
        } else {
          // Timeout alcanzado
          setShouldPoll(false);
          onTimeout?.();
          return false;
        }
      } else {
        // Tareas completadas
        setShouldPoll(false);
        if (!hasCalledSuccessRef.current) {
          hasCalledSuccessRef.current = true;
          onSuccess?.(data);
        }
        return false;
      }
    },
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 0,
  });

  // Manejar errores
  if (tasksStatus.error && onError) {
    onError(tasksStatus.error as Error);
  }
  const startPolling = useCallback(() => {
    setShouldPoll(true);
    attemptsRef.current = 0;
    hasCalledSuccessRef.current = false;
  }, []);

  const stopPolling = useCallback(() => {
    setShouldPoll(false);
  }, []);

  const reset = useCallback(() => {
    setShouldPoll(false);
    attemptsRef.current = 0;
    hasCalledSuccessRef.current = false;
  }, []);

  const currentHasRunningTasks = hasRunningTasks(tasksStatus.data?.tasks);
  const allTasksCompleted = tasksStatus.data?.tasks?.length === 0;
  const isComplete = !currentHasRunningTasks && !shouldPoll && !!tasksStatus.data;
  const isTimeout = attemptsRef.current >= maxAttempts && !shouldPoll && currentHasRunningTasks;

  return {
    startPolling,
    stopPolling,
    isPolling: shouldPoll,
    tasksData: tasksStatus.data,
    attempts: attemptsRef.current,
    maxAttempts,
    isComplete,
    isTimeout,
    isLoading: tasksStatus.isLoading,
    error: tasksStatus.error as Error | null,
    reset,
    hasRunningTasks: currentHasRunningTasks,
    allTasksCompleted,
  };
};
